import { QueryClientProvider } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { apiClient } from "@/api/client";
import { apiErrorMessage } from "@/api/errors";
import { createQueryClient } from "@/api/query-client";
import type { components } from "@/api/schema";
import { AuthProvider } from "@/shared/auth";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { toast } from "@/shared/lib/toast";

type AuthUser = components["schemas"]["AuthUser"];

interface RouterContext {
  // Placeholder for future auth/user context.
  readonly _placeholder?: never;
}

function RootLayout(): JSX.Element {
  const [queryClient] = useState(() =>
    createQueryClient({ onMutationError: (e) => toast.error(apiErrorMessage(e)) }),
  );
  const { t } = useTranslation();
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider
        verifyToken={async (): Promise<AuthUser | null> => {
          try {
            const { data, error } = await apiClient.GET("/v1/auth/me");
            if (error || !data) return null;
            return data.user;
          } catch {
            return null;
          }
        }}
        postLogout={async (): Promise<void> => {
          await apiClient.POST("/v1/auth/logout", {}).catch(() => null);
        }}
      >
        <ErrorBoundary fallback={<p data-testid="root-error">{t("state.error")}</p>}>
          <div className="min-h-screen bg-background text-foreground">
            <Outlet />
          </div>
        </ErrorBoundary>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function NotFound(): JSX.Element {
  return (
    <main className="flex min-h-screen items-center justify-center">
      <p data-testid="not-found">Page not found</p>
    </main>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
});
